import { Box, Grid, Typography } from '@mui/material';
import { FunctionComponent } from 'react';
import { SpringZoom } from '../../components/animation/AnimatedContainers';
import { GameLogoScreenLayout } from '../../components/game/GameLogoLayout';
import { ScoreBox, useTeamScoreBoxOuterStyle } from '../../components/game/Scores';
import { Sound, useMusic } from '../../Sound';
import { gameConfigStore, useGameConfigSelector } from '../../store/GameConfigStore';
import { gamePlayStore, useGameplaySelector } from '../../store/GameplayStore';
import { Team } from '../../types/GameplayTypes';
import { muiStyleSheet } from '../../util/Styles';

export const GameFinishedScreen: FunctionComponent = () => {
	useMusic(Sound.music.Winner);

	const teamNames = useGameConfigSelector(gameConfigStore.selectors.teamNames);
	const teamScores = useGameplaySelector(gamePlayStore.selectors.teamScores);

	const winningTeam = teamScores[Team.A] > teamScores[Team.B] ? Team.A : Team.B;
	const isDraw = teamScores[Team.A] === teamScores[Team.B];

	return <GameLogoScreenLayout title='Results'>
		<Grid container columns={7} spacing={2} sx={styles.content}>
			<Grid size={1} offset={1} sx={styles.gridItem}>
				<TeamFinalScore team={Team.A} winner={!isDraw && (winningTeam === Team.A)} />
			</Grid>
			<Grid size={3} sx={styles.gridItem}>
				<Typography variant="h1" sx={styles.outcome}			>
					{isDraw ? "It's a Draw!" : `${teamNames[winningTeam]} Wins!`}
				</Typography>
			</Grid>
			<Grid size={1} sx={styles.gridItem}>
				<TeamFinalScore team={Team.B} winner={!isDraw && (winningTeam === Team.B)} />
			</Grid>
			<Grid size={1} offset={1} sx={styles.gridItem}>
				<TeamName team={Team.A} winner={!isDraw && (winningTeam === Team.A)} />
			</Grid>
			<Grid size={1} offset={3} sx={styles.gridItem}>
				<TeamName team={Team.B} winner={!isDraw && (winningTeam === Team.B)} />
			</Grid>
		</Grid>
	</GameLogoScreenLayout>;
};

type TeamProps = {
	team: Team;
	winner: boolean;
};

const TeamFinalScore: FunctionComponent<TeamProps> = (props) => {
	const { team, winner } = props;
	const teamScores = useGameplaySelector(gamePlayStore.selectors.teamScores);
	const teamStyle = useTeamScoreBoxOuterStyle(team);

	return <SpringZoom expanded={props.winner} minScale={0.8} >
		<Box sx={[styles.scoreBox, teamStyle, winner && styles.scoreBoxWinner]}>
			<ScoreBox team={team} score={teamScores[team]} digits={3} />
		</Box>
	</SpringZoom>;
};

const TeamName: FunctionComponent<TeamProps> = (props) => {
	const teamNames = useGameConfigSelector(gameConfigStore.selectors.teamNames);

	return <Typography variant="h2" sx={[styles.teamName, props.winner && styles.teamNameWinner]}		>
		{teamNames[props.team]}
	</Typography>;
};

const styles = muiStyleSheet({
	content: {
		position: 'absolute',
		left: 0, right: 0, bottom: 0,
		display: 'flex',
		paddingY: '32px',
		background: 'linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.5) 32px)',
	} as const,
	gridItem: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		textAlign: 'center',
	},
	outcome: theme => ({
		fontWeight: 'bold',
		color: theme.palette.game.white,
		fontSize: '5rem',
		textShadow: '4px 4px 8px rgba(0, 0, 0, 0.5)',
	}),
	scoreBox: theme => ({
		border: `8px solid ${theme.palette.game.white}`,
		borderRadius: '24px',
		alignItems: 'center',
		justifyContent: 'center',
		boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
		display: 'flex',
	}),
	scoreBoxWinner: theme => ({
		border: `8px solid ${theme.palette.game.gold}`,
		boxShadow: `0 8px 24px alpha(from ${theme.palette.game.gold} / 60%)`,
	}),
	teamName: theme => ({
		fontWeight: 'bold',
		color: theme.palette.game.white,
		fontSize: '3rem',
		textShadow: '3px 3px 6px rgba(0, 0, 0, 0.5)',
	}),
	teamNameWinner: theme => ({
		color: theme.palette.game.gold,
	}),
});
